"use client";

import { passwordSchema } from "@repo/api/lib/validation";
import { authClient } from "@repo/auth/client";
import { config } from "@repo/config";
import { getAuthErrorMessage, useSession } from "@saas/auth/client";
import { AuthHeading } from "@saas/shared/components/AuthWrapper";
import { useForm, useStore } from "@tanstack/react-form";
import { useSearch } from "@tanstack/react-router";
import { Button } from "@ui/components/button";
import { Field, FieldError, FieldLabel } from "@ui/components/field";
import { LinkButton } from "@ui/components/link-button";
import { PasswordInput } from "@ui/components/password-input";
import { Spinner } from "@ui/components/spinner";
import { CircleCheckIcon, LinkIcon } from "lucide-react";
import { useState } from "react";
import {
	AuthDone,
	AuthError,
	authErrorInput,
	visibleFieldError,
} from "./AuthNotice";
import { PasswordRulesChecklist } from "./signup-steps/PasswordRulesChecklist";

export function ResetPasswordForm() {
	const { user } = useSession();
	const { token } = useSearch({ strict: false }) as { token?: string };
	const [rootError, setRootError] = useState<string | null>(null);
	const [done, setDone] = useState(false);

	const form = useForm({
		defaultValues: { password: "", confirmPassword: "" },
		onSubmit: async ({ value }) => {
			setRootError(null);
			try {
				const { error } = await authClient.resetPassword({
					token,
					newPassword: value.password,
				});
				if (error) {
					throw error;
				}
				setDone(true);
			} catch (error) {
				setRootError(getAuthErrorMessage(authErrorInput(error)));
			}
		},
	});

	const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
	const attempted = useStore(
		form.store,
		(state) => state.submissionAttempts > 0,
	);
	const password = useStore(form.store, (state) => state.values.password);

	if (!token) {
		return (
			<AuthDone
				icon={<LinkIcon />}
				title="This reset link is incomplete"
				actions={
					<LinkButton to="/forgot-password">
						Send a new link
					</LinkButton>
				}
			>
				Open the link from your email again, or request a new one.
			</AuthDone>
		);
	}

	if (done) {
		return (
			<AuthDone
				icon={<CircleCheckIcon />}
				title="Password updated"
				actions={
					user ? (
						<LinkButton to={config.auth.redirectAfterSignIn}>
							Open the admin
						</LinkButton>
					) : (
						<LinkButton to="/login">Sign in</LinkButton>
					)
				}
			>
				Use your new password the next time you sign in.
			</AuthDone>
		);
	}

	return (
		<div className="flex flex-col">
			<AuthHeading title="Choose a new password" />
			<form
				className="flex flex-col gap-5"
				noValidate
				onSubmit={(event) => {
					event.preventDefault();
					event.stopPropagation();
					form.handleSubmit();
				}}
			>
				<AuthError message={rootError} />
				<form.Field
					name="password"
					validators={{ onBlur: passwordSchema }}
				>
					{(field) => {
						const error = visibleFieldError(field, attempted);
						const invalid = !!error;
						return (
							<Field data-invalid={invalid || undefined}>
								<FieldLabel htmlFor="password">
									New password
								</FieldLabel>
								<PasswordInput
									id="password"
									value={field.state.value}
									onChange={field.handleChange}
									onBlur={field.handleBlur}
									autoComplete="new-password"
									autoFocus
									aria-invalid={invalid || undefined}
								/>
								<PasswordRulesChecklist password={password} />
								{invalid ? <FieldError errors={error} /> : null}
							</Field>
						);
					}}
				</form.Field>
				<form.Field
					name="confirmPassword"
					validators={{
						onBlur: ({ value, fieldApi }) =>
							!value
								? "Type the new password again"
								: value !==
										fieldApi.form.getFieldValue("password")
									? "The two passwords don't match"
									: undefined,
					}}
				>
					{(field) => {
						const error = visibleFieldError(field, attempted);
						const invalid = !!error;
						return (
							<Field data-invalid={invalid || undefined}>
								<FieldLabel htmlFor="confirmPassword">
									Confirm new password
								</FieldLabel>
								<PasswordInput
									id="confirmPassword"
									value={field.state.value}
									onChange={field.handleChange}
									onBlur={field.handleBlur}
									autoComplete="new-password"
									aria-invalid={invalid || undefined}
								/>
								{invalid ? (
									<FieldError
										errors={field.state.meta.errors.map(
											(message) => ({
												message: String(message),
											}),
										)}
									/>
								) : null}
							</Field>
						);
					}}
				</form.Field>
				<Button type="submit" size="lg" disabled={isSubmitting}>
					{isSubmitting ? <Spinner data-icon="inline-start" /> : null}
					Save new password
				</Button>
			</form>
		</div>
	);
}
